/**
 * Product Gallery with PhotoSwipe
 * Opens a fullscreen lightbox when clicking on product images
 */
(function() {
  'use strict';

  var isInitialized = false;
  var GALLERY_SELECTOR = '.product__media-list.js-media-list';
  var GALLERY_ID_PREFIX = 'pswp-product-gallery';
  // Product pages render one .js-media-list per responsive layout (mobile + desktop),
  // toggled with display:none/block — both exist in the DOM at once. Each needs its
  // own PhotoSwipe instance, keyed by its media list element.
  var galleries = new Map();

  /**
   * Convert image URL to high-res gallery version
   */
  function getHighResUrl(url) {
    if (url && url.indexOf('ondigitalocean.app/images/') !== -1) {
      return url.replace('/images/', '/gallery/');
    }
    return url;
  }

  /**
   * Setup gallery structure for PhotoSwipe
   */
  function setupGallery(mediaList, index) {
    var wrapper = mediaList.querySelector('.swiper-wrapper');
    if (!wrapper) return null;

    // Set gallery ID (unique per media list instance)
    wrapper.id = GALLERY_ID_PREFIX + '-' + index;

    // Get all slides (excluding duplicates)
    var slides = wrapper.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)');

    slides.forEach(function(slide) {
      var mediaDiv = slide.querySelector('.product__media');
      var img = mediaDiv ? mediaDiv.querySelector('img') : slide.querySelector('img');

      if (!img || !img.src) return;

      // Skip if already set up
      if (img.hasAttribute('data-pswp-src')) return;

      // Add PhotoSwipe data attributes to the image
      img.setAttribute('data-pswp-src', getHighResUrl(img.src));

      // Get dimensions - use naturalWidth/Height if loaded, otherwise estimate from displayed size
      var width = img.naturalWidth || img.width || 1200;
      var height = img.naturalHeight || img.height || 1200;

      // If image not loaded yet, update dimensions when it loads
      if (!img.complete || img.naturalWidth === 0) {
        img.addEventListener('load', function() {
          img.setAttribute('data-pswp-width', img.naturalWidth || 1200);
          img.setAttribute('data-pswp-height', img.naturalHeight || 1200);
        });
      }

      img.setAttribute('data-pswp-width', width);
      img.setAttribute('data-pswp-height', height);
    });

    return wrapper;
  }

  /**
   * Get slide images for PhotoSwipe from a specific gallery wrapper
   */
  function getSlideImages(wrapper) {
    if (!wrapper) return [];
    return Array.from(wrapper.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate) .product__media img[data-pswp-src]'));
  }

  /**
   * Initialize PhotoSwipe lightbox for a specific gallery wrapper
   */
  function initLightbox(wrapper) {
    if (typeof PhotoSwipeLightbox === 'undefined') {
      console.error('PhotoSwipe: Library not loaded');
      return null;
    }

    var images = getSlideImages(wrapper);
    if (images.length === 0) return null;

    // Build data source from images
    var dataSource = images.map(function(img) {
      return {
        src: img.getAttribute('data-pswp-src'),
        msrc: img.src,
        width: parseInt(img.getAttribute('data-pswp-width')) || 1200,
        height: parseInt(img.getAttribute('data-pswp-height')) || 1200,
        alt: img.alt || '',
        element: img
      };
    });

    var lightbox = new PhotoSwipeLightbox({
      dataSource: dataSource,
      pswpModule: PhotoSwipe,
      bgOpacity: 0.9,
      showHideAnimationType: 'fade',
      initialZoomLevel: 'fit',
      secondaryZoomLevel: 2,
      maxZoomLevel: 4,
      imageClickAction: 'zoom',
      tapAction: 'zoom',
      doubleTapAction: 'zoom',
      closeOnVerticalDrag: true,
      padding: { top: 20, bottom: 20, left: 20, right: 20 }
    });

    // Return placeholder while loading
    lightbox.addFilter('placeholderSrc', function(placeholderSrc, slide) {
      if (slide.data && slide.data.msrc) {
        return slide.data.msrc;
      }
      return placeholderSrc;
    });

    lightbox.init();
    return lightbox;
  }

  /**
   * Drop entries whose media list element is no longer attached to the page
   * (e.g. replaced wholesale by a variant-switch re-render), so switching
   * variants repeatedly doesn't accumulate stale PhotoSwipe instances.
   */
  function pruneDetachedGalleries() {
    galleries.forEach(function(entry, mediaList) {
      if (!document.body.contains(mediaList)) {
        galleries.delete(mediaList);
      }
    });
  }

  /**
   * Get (or lazily create) the gallery entry for a media list element.
   * Variant switches re-render the media list via innerHTML, producing a new
   * element each time, so this can't rely solely on the one-time setup() pass.
   */
  function getGalleryEntry(mediaList) {
    pruneDetachedGalleries();
    var entry = galleries.get(mediaList);
    if (!entry) {
      var wrapper = setupGallery(mediaList, galleries.size);
      if (!wrapper) return null;
      entry = { wrapper: wrapper, lightbox: null };
      galleries.set(mediaList, entry);
    }
    if (!entry.lightbox) {
      entry.lightbox = initLightbox(entry.wrapper);
    }
    return entry;
  }

  /**
   * Open the gallery belonging to a specific media list at a given index.
   * Falls back to the first known gallery when no element is passed.
   */
  function openGallery(index, mediaList) {
    mediaList = mediaList || document.querySelector(GALLERY_SELECTOR);
    if (!mediaList) return;

    var entry = getGalleryEntry(mediaList);
    if (!entry || !entry.lightbox) return;

    var images = getSlideImages(entry.wrapper);
    if (images.length === 0) return;

    // Ensure index is valid
    if (typeof index !== 'number' || index < 0 || index >= images.length) {
      index = 0;
    }

    entry.lightbox.loadAndOpen(index);
  }

  /**
   * Setup every gallery instance on the page (e.g. separate mobile/desktop
   * media lists toggled with display:none/block at a breakpoint)
   */
  function setup() {
    var mediaLists = document.querySelectorAll(GALLERY_SELECTOR);
    if (mediaLists.length === 0) return;

    mediaLists.forEach(function(mediaList, index) {
      var wrapper = setupGallery(mediaList, index);
      if (!wrapper) return;

      galleries.set(mediaList, {
        wrapper: wrapper,
        lightbox: initLightbox(wrapper)
      });

      // Style images as clickable with pointer cursor
      mediaList.querySelectorAll('.product__media').forEach(function(el) {
        el.style.cursor = 'pointer';
      });
    });

    // Expose openGallery globally for Swiper click handler / zoom button
    window.openProductGallery = openGallery;
  }

  /**
   * Load PhotoSwipe scripts
   */
  function loadPhotoSwipe(callback) {
    if (typeof PhotoSwipeLightbox !== 'undefined' && typeof PhotoSwipe !== 'undefined') {
      callback();
      return;
    }

    var lightboxUrl = window.photoswipeLightboxUrl;
    var photoswipeUrl = window.photoswipeUrl;
    if (!lightboxUrl || !photoswipeUrl) {
      console.error('PhotoSwipe: URLs not configured');
      return;
    }

    var script = document.createElement('script');
    script.type = 'module';
    script.textContent = `
      import PhotoSwipeLightbox from '${lightboxUrl}';
      import PhotoSwipe from '${photoswipeUrl}';
      window.PhotoSwipeLightbox = PhotoSwipeLightbox;
      window.PhotoSwipe = PhotoSwipe;
      window.dispatchEvent(new Event('photoswipe-loaded'));
    `;
    document.head.appendChild(script);
    window.addEventListener('photoswipe-loaded', callback, { once: true });
  }

  /**
   * Initialize
   */
  function init() {
    if (isInitialized) return;

    var mediaList = document.querySelector(GALLERY_SELECTOR);
    if (mediaList && mediaList.querySelector('.swiper-wrapper')) {
      isInitialized = true;
      loadPhotoSwipe(setup);
    } else {
      var checkReady = setInterval(function() {
        var mediaList = document.querySelector(GALLERY_SELECTOR);
        if (mediaList && mediaList.querySelector('.swiper-wrapper')) {
          clearInterval(checkReady);
          isInitialized = true;
          loadPhotoSwipe(setup);
        }
      }, 100);
      setTimeout(function() { clearInterval(checkReady); }, 5000);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
